import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, RotateCcw } from 'lucide-react';
import { errorDev } from '../utils/logger.js';

/**
 * Reset confirmation modal
 * Displays warning about resetting preferences to defaults
 */
const ResetConfirmModal = ({ 
  title = 'Reset to Defaults',
  description = 'Reset all settings to defaults',
  message = 'Are you sure? This cannot be undone.',
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  const { t, ready: i18nReady } = useTranslation(['settings', 'common']);
  const [isProcessing, setIsProcessing] = useState(false);

  // Safe translation function with fallback
  const safeT = (key, fallback = key, namespace = undefined) => {
    if (!i18nReady) return fallback;
    try {
      const result = namespace ? t(key, { ns: namespace }) : t(key);
      return result === key ? fallback : result;
    } catch (err) {
      errorDev("Operation failed:", err);
      return fallback;
    }
  };

  const handleConfirm = async () => {
    if (isProcessing || isLoading) return;
    
    setIsProcessing(true);
    try {
      await onConfirm();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-md w-full p-6 shadow-xl animate-in fade-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle size={24} className="text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {safeT("data.resetTitle", "Reset to Defaults")}
            </h2>
          </div>
          <button
            onClick={onCancel}
            disabled={isProcessing || isLoading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
            aria-label={safeT('close', 'Close', 'common')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Subtitle */}
        <div className="mb-2">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {safeT('data.resetDescription', 'Reset all settings to defaults')}
          </p>
        </div>

        {/* Warning Message */}
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-amber-900 dark:text-amber-100 text-sm leading-relaxed">
            {safeT('data.resetMessage', 'Are you sure? This cannot be undone.')}
          </p>
        </div>

        {/* Consequences */}
        <div className="mb-6 space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {safeT('data.resetConsequences', 'This will reset:')}
          </h3>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li className="flex items-start gap-2">
              <span className="text-amber-600 mt-1">•</span>
              <span>{safeT('data.resetConsequence1', 'All your preferences and settings')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-600 mt-1">•</span>
              <span>{safeT('data.resetConsequence2', 'Language and theme preferences')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-600 mt-1">•</span>
              <span>{safeT('data.resetConsequence3', 'Notification settings')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-600 mt-1">•</span>
              <span>{safeT('data.resetConsequence4', 'All custom configurations')}</span>
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isProcessing || isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {safeT('cancel', 'Cancel', 'common')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isProcessing || isLoading}
            className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-500 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium text-sm flex items-center justify-center gap-2"
          >
            {isProcessing || isLoading ? (
              <>
                <div className="orrith-loader orrith-loader-xs orrith-loader-on-dark"></div>
                <span>{safeT('resetting', 'Resetting...', 'common')}</span>
              </>
            ) : (
              <>
                <RotateCcw size={16} />
                <span>{safeT('data.resetButton', 'Reset to Defaults')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetConfirmModal;